import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Stack, Chip, CircularProgress,
  Select, MenuItem, FormControl, Divider, Tooltip, IconButton,
  Checkbox, Snackbar, Alert, TextField,
} from "@mui/material";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridOnIcon from "@mui/icons-material/GridOn";
import StorageIcon from "@mui/icons-material/Storage";
import KeyIcon from "@mui/icons-material/VpnKey";
import VerifiedIcon from "@mui/icons-material/Verified";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Menu, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
         List, ListItemButton, ListItemText, ToggleButton, ToggleButtonGroup } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteOutlineIcon from "@mui/icons-material/Delete";
import { listReports, getReport, saveReport, deleteReport } from "../api/reportsApi";
import SpeedIcon from "@mui/icons-material/Speed";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import TimelineIcon from "@mui/icons-material/Timeline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FunctionsIcon from "@mui/icons-material/Functions";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import GetAppIcon from "@mui/icons-material/GetApp";
import CloseIcon from "@mui/icons-material/Close";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { fetchTables, fetchColumns, fetchDistinct, runMultiQuery, runMultiQueryPivot, runNLQuery, fetchReachable } from "../api/oracleApi";

// ---- Theme constants ----
const PANEL_BG   = "#1e2433";
const PANEL_TEXT = "#c9d1e0";
const ACCENT     = "#3b7cf7";
const MEAS_C     = "#2563eb";
const PIE_COLORS = ["#2563eb","#7c3aed","#0891b2","#059669","#d97706","#dc2626","#db2777","#4f46e5","#0d9488","#65a30d","#a16207","#0e7490"];
const AGGS       = ["SUM","AVG","COUNT","MIN","MAX"];

const CHART_TYPES = [
  { id:"bar",     icon:<BarChartIcon sx={{fontSize:18}}/>,     label:"Bar" },
  { id:"line",    icon:<ShowChartIcon sx={{fontSize:18}}/>,    label:"Line" },
  { id:"area",    icon:<TimelineIcon sx={{fontSize:18}}/>,     label:"Area" },
  { id:"pie",     icon:<PieChartIcon sx={{fontSize:18}}/>,     label:"Pie" },
  { id:"scatter", icon:<ScatterPlotIcon sx={{fontSize:18}}/>,  label:"Scatter" },
  { id:"table",   icon:<TableRowsIcon sx={{fontSize:18}}/>,    label:"Table" },
  { id:"pivot",   icon:<GridOnIcon sx={{fontSize:18}}/>,       label:"Pivot" },
  { id:"kpi",     icon:<SpeedIcon sx={{fontSize:18}}/>,        label:"KPI" },
];

// ---- Helpers ----
const fmt = (v, compact = false) => {
  if (v == null) return "";
  if (typeof v === "number") {
    if (compact && Math.abs(v) >= 1_000_000) return (v/1_000_000).toFixed(2)+"M";
    if (compact && Math.abs(v) >= 1_000)     return (v/1_000).toFixed(1)+"K";
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
};

// format a measure value per the user's chosen "Edit format" option
const formatValue = (v, format) => {
  if (v == null || typeof v !== "number") return fmt(v);
  switch (format) {
    case "Currency": return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    case "Percent": return (v * 100).toFixed(1) + "%";
    case "Thousands (K)": return (v / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "K";
    case "Millions (M)": return (v / 1e6).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "M";
    case "Number": return v.toLocaleString();
    default: return fmt(v);
  }
};

const truncate = (s, n = 16) => s && s.length > n ? s.slice(0, n) + "…" : s;

// ---- Droppable well ----
// ---- OAC-style criteria card (a selected column) ----
const FORMATS = ["Currency", "Number", "Percent", "Thousands (K)", "Millions (M)"];
function CriteriaCard({ label, kind, onRemove, onRename, onAddFilter, onFormat, currentFormat, onMove, moveLabel }) {
  const C = { measure: ["#dbeafe", "#1565c0"], dimension: ["#dcfce7", "#2e7d32"],
              column: ["#ede7f6", "#5e35b1"], filter: ["#fff3e0", "#e65100"] }[kind] || ["#eee", "#555"];
  const tag = { measure: "Measure", dimension: "Attribute", column: "Column", filter: "Filter" }[kind] || "";
  const [anchor, setAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const hasMenu = onRename || onAddFilter || onFormat || onMove;
  const startRename = () => { setDraft(label); setEditing(true); setAnchor(null); };
  const commit = () => { if (onRename && draft.trim()) onRename(draft.trim()); setEditing(false); };
  return (
    <Box sx={{ display:"flex", flexDirection:"column", border:`1px solid ${C[0]}`, borderRadius:1.5,
               bgcolor:"#fff", minWidth:130, overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                 bgcolor:C[0], px:1, py:0.25 }}>
        <Typography sx={{ fontSize:9, fontWeight:700, color:C[1], textTransform:"uppercase", letterSpacing:0.4 }}>
          {tag}{currentFormat ? ` · ${currentFormat}` : ""}
        </Typography>
        <Box sx={{ display:"flex", alignItems:"center" }}>
          {hasMenu && (
            <MoreVertIcon onClick={e => setAnchor(e.currentTarget)}
              sx={{ fontSize:14, color:C[1], cursor:"pointer", "&:hover":{ opacity:0.6 } }}/>
          )}
          <CloseIcon onClick={onRemove} sx={{ fontSize:13, color:C[1], cursor:"pointer", "&:hover":{ opacity:0.6 } }}/>
        </Box>
      </Box>
      {editing ? (
        <TextField size="small" autoFocus value={draft} onChange={e => setDraft(e.target.value)}
          onBlur={commit} onKeyDown={e => { if (e.key==="Enter") commit(); if (e.key==="Escape") setEditing(false); }}
          sx={{ m:0.5, "& .MuiInputBase-input":{ fontSize:11.5, py:0.4 } }}/>
      ) : (
        <Typography sx={{ px:1, py:0.5, fontSize:12, fontFamily:"monospace", color:"#222", whiteSpace:"nowrap" }}>
          {label}
        </Typography>
      )}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {onRename && <MenuItem onClick={startRename} sx={{ fontSize:13, gap:1 }}><EditIcon sx={{fontSize:15}}/>Rename column</MenuItem>}
        {onAddFilter && <MenuItem onClick={() => { onAddFilter(); setAnchor(null); }} sx={{ fontSize:13, gap:1 }}><FilterAltIcon sx={{fontSize:15}}/>Add filter</MenuItem>}
        {onMove && <MenuItem onClick={() => { onMove(); setAnchor(null); }} sx={{ fontSize:13, gap:1 }}><ViewColumnIcon sx={{fontSize:15}}/>{moveLabel}</MenuItem>}
        {onFormat && <Divider />}
        {onFormat && <Typography sx={{ px:2, py:0.5, fontSize:10, color:"#999", fontWeight:700 }}>EDIT FORMAT</Typography>}
        {onFormat && FORMATS.map(f => (
          <MenuItem key={f} selected={currentFormat===f} onClick={() => { onFormat(f); setAnchor(null); }}
            sx={{ fontSize:13 }}>{f}</MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// ---- Pivot table ----
function PivotTable({ data, rowField, colField, rowLabel, colLabel, measureCol, measureLabel }) {
  if (!data?.length || !rowField || !colField) return (
    <Typography sx={{ p:3, color:"#888", textAlign:"center" }}>
      Add a dimension to COLUMNS well to build pivot.
    </Typography>
  );
  const rowKeys = [...new Set(data.map(r => r[rowField]))].filter(Boolean).sort();
  const colKeys = [...new Set(data.map(r => r[colField]))].filter(Boolean).sort();
  const lookup = {};
  data.forEach(r => { lookup[`${r[rowField]}|||${r[colField]}`] = Number(r[measureCol] || 0); });
  const rowTotals = {};
  rowKeys.forEach(rk => { rowTotals[rk] = colKeys.reduce((s,ck) => s + (lookup[`${rk}|||${ck}`]||0), 0); });
  const colTotals = {};
  colKeys.forEach(ck => { colTotals[ck] = rowKeys.reduce((s,rk) => s + (lookup[`${rk}|||${ck}`]||0), 0); });
  const grand = rowKeys.reduce((s,rk) => s + (rowTotals[rk]||0), 0);

  const TH = ({ ch, right, wide }) => (
    <th style={{ padding:"7px 12px", background:"#37474f", color:"#fff", fontWeight:600,
                 fontSize:12, textAlign: right?"right":"left", whiteSpace:"nowrap",
                 borderRight:"1px solid #546e7a", minWidth: wide?140:100 }}>
      {ch}
    </th>
  );
  const TD = ({ ch, right, alt, bold }) => (
    <td style={{ padding:"5px 12px", fontSize:12, fontFamily:"monospace",
                 textAlign: right?"right":"left", background: alt?"#f0f4f8":"#fff",
                 borderRight:"1px solid #eceff1", borderBottom:"1px solid #eceff1",
                 fontWeight: bold?700:"normal", whiteSpace:"nowrap" }}>
      {ch}
    </td>
  );

  return (
    <Box sx={{ overflow:"auto" }}>
      <table style={{ borderCollapse:"collapse", width:"100%" }}>
        <thead>
          <tr>
            <TH ch={rowLabel || rowField} wide />
            {colKeys.map(ck => <TH key={ck} ch={ck} right />)}
            <TH ch="Total" right />
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((rk, ri) => (
            <tr key={rk}>
              <TD ch={rk} alt={ri%2===0} bold />
              {colKeys.map(ck => (
                <TD key={ck} right alt={ri%2===0}
                  ch={lookup[`${rk}|||${ck}`] ? fmt(lookup[`${rk}|||${ck}`]) : "—"} />
              ))}
              <TD right alt={ri%2===0} bold ch={fmt(rowTotals[rk])} />
            </tr>
          ))}
          <tr>
            <TD ch="Total" alt bold />
            {colKeys.map(ck => <TD key={ck} right alt bold ch={fmt(colTotals[ck])} />)}
            <TD right alt bold ch={fmt(grand)} />
          </tr>
        </tbody>
      </table>
    </Box>
  );
}

// ---- KPI Card ----
function KPICard({ title, value, agg, color = MEAS_C }) {
  return (
    <Paper elevation={2} sx={{
      p: 2.5, borderRadius: 2, minWidth: 160, flex: "1 1 160px",
      borderTop: `4px solid ${color}`, textAlign:"center",
    }}>
      <Typography sx={{ fontSize:10, fontWeight:700, color:"#888", letterSpacing:1, textTransform:"uppercase" }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize:28, fontWeight:800, color:color, my:0.5 }}>
        {fmt(value, true)}
      </Typography>
      <Typography sx={{ fontSize:11, color:"#aaa" }}>{agg}</Typography>
    </Paper>
  );
}

// ---- AI Query bar ----
function AIQueryBar({ onResult, onError }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  return (
    <Paper variant="outlined" sx={{ p:1.5, mb:0, bgcolor:"#fffde7", borderColor:"#ffd54f" }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoFixHighIcon sx={{ color:"#f57f17", fontSize:20 }} />
        <TextField
          size="small" placeholder='Ask a question e.g. "Show total sales by product type"'
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleRun(); }}
          sx={{ flex:1, "& .MuiInputBase-input": { fontSize:13 } }}
        />
        <Button size="small" variant="contained" disabled={!q.trim() || loading}
          onClick={handleRun} sx={{ bgcolor:"#f57f17", "&:hover":{bgcolor:"#e65100"}, whiteSpace:"nowrap" }}
          startIcon={loading ? <CircularProgress size={14} color="inherit"/> : <AutoFixHighIcon/>}>
          {loading ? "Thinking…" : "AI Query"}
        </Button>
      </Stack>
    </Paper>
  );

  async function handleRun() {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const d = await runNLQuery(q.trim());
      onResult(d);
    } catch (e) {
      onError(e.message);
    }
    setLoading(false);
  }
}

// ---- Data table (enterprise, zebra-striped) ----
// internal plumbing keys that must never become display columns
const HIDDEN_KEYS = new Set(["value", "values"]);
function DataTable({ data, labels }) {
  if (!data?.length) return null;
  const cols = Object.keys(data[0]).filter(c => !HIDDEN_KEYS.has(c));
  const isNum = c => typeof data[0][c] === "number";
  return (
    <Box sx={{ overflowX:"auto", overflowY:"auto", maxHeight:520, border:"1px solid #e3e7ed", borderRadius:1.5 }}>
      {/* width:auto + min column widths => columns size to content, no huge gap, and
          the container scrolls horizontally when there are many columns */}
      <table style={{ borderCollapse:"collapse", width:"auto", minWidth:"100%", fontSize:12.5 }}>
        <thead>
          <tr>
            {cols.map((c, ci) => (
              <th key={c} style={{ padding:"9px 16px", background:"#233055", color:"#f1f5fa",
                                    fontWeight:700, fontSize:12, letterSpacing:0.2,
                                    textAlign: isNum(c) ? "right" : "left", whiteSpace:"nowrap",
                                    minWidth: isNum(c) ? 130 : 150, borderRight:"1px solid #34406b",
                                    position:"sticky", top:0, zIndex:1 }}>
                {(labels && labels[c]) || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} style={{ background: i%2===0 ? "#f6f8fc" : "#ffffff" }}>
              {cols.map(c => (
                <td key={c} style={{ padding:"7px 16px",
                                     fontFamily: isNum(c) ? "monospace" : "inherit",
                                     color: isNum(c) ? "#1a2a52" : "#333",
                                     fontWeight: isNum(c) ? 600 : 400,
                                     borderRight:"1px solid #eef1f6", borderBottom:"1px solid #eef1f6",
                                     textAlign: isNum(c) ? "right" : "left", whiteSpace:"nowrap" }}>
                  {fmt(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================
export default function Analysis() {
  // Data tree
  const [tables, setTables]             = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [expanded, setExpanded]         = useState(new Set());
  const [collapsedSchemas, setCollapsedSchemas] = useState(new Set());
  const [showSql, setShowSql] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);   // friendly vs DW physical names
  const [reachable, setReachable] = useState(null);          // Set of tables combinable with the chosen measure (null = none chosen)

  // can this table be combined with the current measure? (true if no measure yet)
  const isCombinable = (name) => !reachable || reachable.has(name.toUpperCase());
  const reachableRef = useRef(null);
  useEffect(() => { reachableRef.current = reachable; }, [reachable]);
  const [ctxMenu, setCtxMenu] = useState(null);              // right-click "original name" popover

  // display helper: friendly label unless "show original" is on
  const disp = (physical, label) => (showOriginal ? physical : (label || physical));
  const openCtx = (e, physical, label) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, physical, label });
  };

  // ---- Report catalog (Supabase: shared / custom folders) ----
  const [saveDlg, setSaveDlg] = useState(false);
  const [openDlg, setOpenDlg] = useState(false);
  const [reportName, setReportName] = useState("");
  const [saveFolder, setSaveFolder] = useState("custom");
  const [currentReportId, setCurrentReportId] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [autoRun, setAutoRun] = useState(0);

  const buildDefinition = () => ({
    valueFields, rowFields, colFields, agg, chartType,
    filters: activeFilters.map(f => ({ table: f.table, column: f.column, values: f.values })),
  });

  const doSave = async () => {
    if (!reportName.trim()) { setErr("Give the report a name."); return; }
    try {
      const saved = await saveReport({
        id: currentReportId, name: reportName.trim(), folder: saveFolder,
        owner: "veerendrachundru2@gmail.com", definition: buildDefinition(),
      });
      setCurrentReportId(saved?.id || currentReportId);
      setSaveDlg(false);
      setMsg(`Saved “${reportName.trim()}” to ${saveFolder} folder`);
    } catch (e) { setErr(e.message); }
  };

  const openReportsBrowser = async () => {
    setOpenDlg(true); setReportsLoading(true);
    try { setReportsList(await listReports()); }
    catch (e) { setErr(e.message); }
    setReportsLoading(false);
  };

  const loadReport = async (id) => {
    try {
      const rep = await getReport(id);
      const def = rep.definition || {};
      setValueFields(def.valueFields || []);
      setRowFields(def.rowFields || []);
      setColFields(def.colFields || []);
      setAgg(def.agg || "SUM");
      setChartType(def.chartType || "bar");
      const filters = def.filters || [];
      const withOpts = await Promise.all(filters.map(async f => {
        try { const d = await fetchDistinct(f.table, f.column); return { ...f, options: d.values }; }
        catch { return { ...f, options: [] }; }
      }));
      setActiveFilters(withOpts);
      setCurrentReportId(rep.id);
      setReportName(rep.name);
      setSaveFolder(rep.folder || "custom");
      setOpenDlg(false);
      setMsg(`Loaded “${rep.name}”`);
      setAutoRun(n => n + 1);   // re-run with the loaded config
    } catch (e) { setErr(e.message); }
  };

  const removeReport = async (id, ev) => {
    ev.stopPropagation();
    try { await deleteReport(id); setReportsList(p => p.filter(r => r.id !== id)); }
    catch (e) { setErr(e.message); }
  };
  const [tableColumns, setTableColumns] = useState({});
  const [loadingCols, setLoadingCols]   = useState({});
  const [search, setSearch]             = useState("");

  // Grammar of Graphics wells
  const [valueFields, setValueFields]   = useState([]);
  const [rowFields, setRowFields]       = useState([]);
  const [colFields, setColFields]       = useState([]);

  // when a measure is chosen, find which tables can join to it (combinability guard)
  useEffect(() => {
    const mt = valueFields[0]?.table;
    if (!mt) { setReachable(null); return; }
    let active = true;
    fetchReachable(mt).then(list => {
      if (active) setReachable(new Set([mt.toUpperCase(), ...list.map(s => s.toUpperCase())]));
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueFields]);

  // If the chosen measure changes so an already-selected dimension no longer
  // connects to it, drop that dimension (and say so) rather than letting the
  // user run a cross-star report that the backend would reject.
  useEffect(() => {
    if (!reachable) return;
    const ok = f => reachable.has(f.table.toUpperCase());
    const bad = [...rowFields.filter(f => !ok(f)), ...colFields.filter(f => !ok(f))];
    if (bad.length) {
      setRowFields(p => p.filter(ok));
      setColFields(p => p.filter(ok));
      setErr(`Removed ${bad.map(f => f.label || f.column).join(", ")} — not connected to `
        + `${valueFields[0]?.label || valueFields[0]?.column}. Pick fields from the same subject area.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachable]);

  // Filters
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Chart
  const [chartType, setChartType]       = useState("bar");
  const [agg, setAgg]                   = useState("SUM");

  // Results
  const [result, setResult]             = useState(null);
  const [aiResult, setAiResult]         = useState(null);
  const [running, setRunning]           = useState(false);
  const [msg, setMsg]                   = useState("");
  const [err, setErr]                   = useState("");

  const dragItem = useRef(null);

  // ---- Resizable panel widths ----
  // Layout order: DATA (leftW) | CANVAS (flex)
  const [leftW, setLeftW]     = useState(300);
  const resizing = useRef(null); // "left" | "config" | null
  const leftWRef = useRef(leftW);
  useEffect(() => { leftWRef.current = leftW; }, [leftW]);

  useEffect(() => {
    const onMove = e => {
      if (!resizing.current) return;
      if (resizing.current === "left") {
        setLeftW(Math.max(180, Math.min(560, e.clientX)));
      }
    };
    const onUp = () => { resizing.current = null; document.body.style.cursor = ""; document.body.style.userSelect = ""; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const startResize = side => e => {
    e.preventDefault();
    resizing.current = side;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    fetchTables()
      .then(t => { setTables(t); setLoadingTables(false); })
      .catch(e => { setErr(e.message); setLoadingTables(false); });
  }, []);

  // ---- Data tree interactions ----
  const toggleTable = useCallback(async name => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); return next; }
      next.add(name); return next;
    });
    if (!tableColumns[name]) {
      setLoadingCols(p => ({ ...p, [name]: true }));
      try {
        const d = await fetchColumns(name);
        setTableColumns(p => ({ ...p, [name]: d }));
      } catch (e) { setErr(e.message); }
      setLoadingCols(p => ({ ...p, [name]: false }));
    }
  }, [tableColumns]);

  // ---- Drag handlers ----
  const handleDragStart = useCallback((e, item) => {
    dragItem.current = item;
    e.dataTransfer.effectAllowed = "copy";
  }, []);

  // ---- Click-to-add (OAC style): click a field, it lands in the right well ----
  const clickAdd = (item) => {
    const key = it => `${it.table}.${it.column}`;
    if (item.kind === "measure") {
      setValueFields(p => p.find(x => key(x) === key(item)) ? p : [...p, item]);
    } else {
      if (!isCombinable(item.table)) {
        setErr(`"${item.table}" can't be combined with your measure — pick a dimension from a connected table (or change the measure).`);
        return;
      }
      setRowFields(p => p.find(x => key(x) === key(item)) ? p : [...p, item]);
    }
  };

  // ---- Inline dropdown filters ----
  // When a dimension is dropped on the Filters well, load its distinct values
  // and add an inline multi-select dropdown the user can pick from.
  const addFilterField = async (item) => {
    if (activeFilters.find(f => f.table === item.table && f.column === item.column)) return;
    setFilterLoading(true);
    try {
      const d = await fetchDistinct(item.table, item.column);
      setActiveFilters(p => [...p, { table: item.table, column: item.column, values: [], options: d.values }]);
    } catch (e) { setErr(e.message); }
    setFilterLoading(false);
  };

  const setFilterValues = (idx, values) =>
    setActiveFilters(p => p.map((f, i) => i === idx ? { ...f, values } : f));

  const removeFilter = (idx) =>
    setActiveFilters(p => p.filter((_, i) => i !== idx));

  // ---- Run query ----
  const run = async () => {
    if (valueFields.length === 0) { setErr("Drag a measure to the VALUES well."); return; }
    if (rowFields.length === 0)   { setErr("Drag at least one dimension to the ROWS well."); return; }
    setRunning(true); setErr(""); setResult(null); setAiResult(null);
    try {
      const vf = valueFields[0];
      const measures = valueFields.map(f => ({ table: f.table, column: f.column }));
      const isPivot = chartType === "pivot" && colFields.length > 0;
      const isColumnar = isPivot || chartType === "table";
      const allDims = isColumnar ? [...rowFields, ...colFields] : rowFields;

      if (isColumnar) {
        const d = await runMultiQueryPivot({
          factTable: vf.table, measures,
          dimensions: allDims.map(f => ({ table: f.table, column: f.column })),
          agg, limit: 500, filters: activeFilters,
        });
        setResult({
          ...d, isPivot,
          rowField: rowFields[0]?.column,
          colField: colFields[0]?.column,
          rowLabel: rowFields[0]?.alias || rowFields[0]?.label || rowFields[0]?.column,
          colLabel: colFields[0]?.alias || colFields[0]?.label || colFields[0]?.column,
        });
        setMsg(`${d.rows} rows returned`);
      } else {
        const d = await runMultiQuery({
          factTable: vf.table, measures,
          dimensions: rowFields.map(f => ({ table: f.table, column: f.column })),
          agg, limit: 100, filters: activeFilters,
        });
        setResult(d);
        setMsg(`${d.rows} rows returned`);
      }
    } catch (e) { setErr(e.message); }
    setRunning(false);
  };

  // re-run automatically after a saved report is loaded
  useEffect(() => {
    if (autoRun && valueFields.length && rowFields.length) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun]);

  // re-run when the view (chart type) or aggregation changes, so the data shape
  // always matches the view (table/pivot = columnar, charts = label/value) and the
  // user NEVER sees stale numbers from a previous view.
  useEffect(() => {
    if (result && valueFields.length && rowFields.length) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, agg]);

  // ---- Export CSV ----
  const exportCsv = () => {
    if (!result?.data?.length) return;
    let csv;
    const d0 = result.data[0];
    if ("label" in d0) {
      const measureLabel = valueFields[0] ? (valueFields[0].alias || `${agg}(${valueFields[0].label || valueFields[0].column})`) : "value";
      csv = ["Dimension," + measureLabel, ...result.data.map(r => `"${r.label}",${r.value}`)].join("\n");
    } else {
      const cols = Object.keys(d0);
      csv = [cols.join(","), ...result.data.map(r => cols.map(c => `"${r[c]??""}"` ).join(","))].join("\n");
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = "report.csv"; a.click();
  };

  const measureLabel = valueFields[0] ? (valueFields[0].alias || `${agg}(${valueFields[0].label || valueFields[0].column})`) : "value";
  const mFmt = valueFields[0]?.format;   // chosen "Edit format" for the measure

  // ---- Chart renderer ----
  const renderCanvas = () => {
    // AI result overrides everything
    if (aiResult) {
      return (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
            <Typography sx={{ fontSize:13, fontWeight:700, color:"#f57f17" }}>
              AI Query Result — {aiResult.rows} rows
            </Typography>
            <Button size="small" onClick={() => setAiResult(null)}>Clear</Button>
          </Stack>
          <DataTable data={aiResult.data} />
          <Typography variant="caption" sx={{ color:"#aaa", mt:1, display:"block", fontFamily:"monospace" }}>
            SQL: {aiResult.sql}
          </Typography>
        </Box>
      );
    }

    if (!result) return (
      <Box sx={{ height:"100%", display:"flex", flexDirection:"column",
                 alignItems:"center", justifyContent:"center", color:"#ccc", gap:2 }}>
        <GridOnIcon sx={{ fontSize:72, color:"#e8ecf0" }} />
        <Typography sx={{ color:"#bbb", fontSize:13 }}>
          1. Expand a table in the Data panel<br/>
          2. Drag measures → VALUES, dimensions → ROWS<br/>
          3. Click Run
        </Typography>
      </Box>
    );

    if (chartType === "kpi") {
      const kpis = result.data?.slice(0,8) || [];
      const total = kpis.reduce((s,r) => s + (r.value||0), 0);
      const colors = [MEAS_C,"#7c3aed","#059669","#d97706","#dc2626","#0891b2","#4f46e5","#db2777"];
      return (
        <Box>
          <Box sx={{ display:"flex", flexWrap:"wrap", gap:2, mb:2 }}>
            <KPICard title={measureLabel + " (Total)"} value={total} agg="Grand Total" color={MEAS_C} />
          </Box>
          <Box sx={{ display:"flex", flexWrap:"wrap", gap:2 }}>
            {kpis.map((r,i) => (
              <KPICard key={i} title={r.label} value={r.value} agg={agg} color={colors[i%colors.length]} />
            ))}
          </Box>
        </Box>
      );
    }

    if (chartType === "pivot") {
      return (
        <PivotTable
          data={result.data}
          rowField={result.rowField}
          colField={result.colField}
          rowLabel={result.rowLabel}
          colLabel={result.colLabel}
          measureCol={result.measure_column || measureLabel}
          measureLabel={measureLabel}
        />
      );
    }

    if (chartType === "table") {
      // map raw column keys -> custom headings (aliases) where the user renamed them
      const labels = {};
      [...rowFields, ...colFields].forEach(f => {
        const friendly = f.alias || f.label;
        if (friendly) labels[f.column.toUpperCase()] = friendly;
      });
      // friendly heading for EVERY measure column (not just the first)
      valueFields.forEach(vf => {
        const mkey = `${agg.toUpperCase()}(${vf.column})`;
        labels[mkey] = `${agg.toUpperCase()}(${vf.alias || vf.label || vf.column})`;
      });
      return <DataTable data={result.data} labels={labels} />;
    }

    // Chart types using {label, value}
    const data = result.data || [];
    const margin = { top:10, right:20, bottom:70, left:20 };
    // multi-measure series (each measure label is a key on every data row)
    const series = (result.measures && result.measures.length > 1) ? result.measures : null;
    const SERIES_COLORS = [MEAS_C, "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2"];

    if (chartType === "pie") {
      return (
        <ResponsiveContainer width="100%" height={420}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="46%"
                 outerRadius={150} label={({ name, percent }) => `${truncate(name,12)}: ${(percent*100).toFixed(1)}%`}
                 labelLine>
              {data.map((_,i) => <Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
            </Pie>
            <RTooltip formatter={v => formatValue(v, mFmt)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "scatter") {
      const scatterData = data.map((r,i) => ({ x: i+1, y: r.value, name: r.label }));
      return (
        <ResponsiveContainer width="100%" height={420}>
          <ScatterChart margin={{ top:20, right:20, bottom:20, left:20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="x" type="number" name="Index" tick={{ fontSize:11 }}/>
            <YAxis dataKey="y" type="number" name={measureLabel} tick={{ fontSize:11 }}/>
            <RTooltip cursor={{ strokeDasharray:"3 3" }}
              content={({ active, payload }) => active && payload?.[0] ? (
                <Paper sx={{ p:1.5, fontSize:12 }}>
                  <div><b>{payload[0].payload.name}</b></div>
                  <div>{measureLabel}: {fmt(payload[0].payload.y)}</div>
                </Paper>
              ) : null} />
            <Scatter name={measureLabel} data={scatterData} fill={MEAS_C} />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "bar") {
      return (
        <ResponsiveContainer width="100%" height={420}>
          <BarChart data={data} margin={margin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="label" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={80}
                   tickFormatter={v => truncate(v, 18)}/>
            <YAxis tick={{ fontSize:11 }} tickFormatter={v => fmt(v, true)}/>
            <RTooltip formatter={v => formatValue(v, mFmt)} labelFormatter={l => l}/>
            {series && <Legend wrapperStyle={{ fontSize:11 }}/>}
            {series
              ? series.map((ml,i) => <Bar key={ml} dataKey={ml} fill={SERIES_COLORS[i%SERIES_COLORS.length]} radius={[3,3,0,0]}/>)
              : <Bar dataKey="value" fill={MEAS_C} radius={[3,3,0,0]}
                     label={data.length<=12 ? { position:"top", fontSize:10, formatter: v => fmt(v,true) } : false}/>}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height={420}>
          <LineChart data={data} margin={margin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="label" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={80}
                   tickFormatter={v => truncate(v, 18)}/>
            <YAxis tick={{ fontSize:11 }} tickFormatter={v => fmt(v,true)}/>
            <RTooltip formatter={v => formatValue(v, mFmt)}/>
            {series && <Legend wrapperStyle={{ fontSize:11 }}/>}
            {series
              ? series.map((ml,i) => <Line key={ml} type="monotone" dataKey={ml} stroke={SERIES_COLORS[i%SERIES_COLORS.length]} strokeWidth={2.5} dot={{ r:3 }}/>)
              : <Line type="monotone" dataKey="value" stroke={MEAS_C} strokeWidth={2.5} dot={{ r:4, fill:MEAS_C }}/>}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "area") {
      return (
        <ResponsiveContainer width="100%" height={420}>
          <AreaChart data={data} margin={margin}>
            <defs>
              <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={MEAS_C} stopOpacity={0.25}/>
                <stop offset="95%" stopColor={MEAS_C} stopOpacity={0.02}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="label" tick={{ fontSize:10 }} angle={-30} textAnchor="end" height={80}
                   tickFormatter={v => truncate(v, 18)}/>
            <YAxis tick={{ fontSize:11 }} tickFormatter={v => fmt(v,true)}/>
            <RTooltip formatter={v => formatValue(v, mFmt)}/>
            <Area type="monotone" dataKey="value" stroke={MEAS_C} fill="url(#ag)" strokeWidth={2.5}/>
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  // Filtered tables for search; once a measure is chosen, also HIDE tables that
  // have no join path to it — so the picker can't offer cross-star fields that
  // would build an invalid report. (Before any measure, isCombinable is true.)
  const visibleTables = tables.filter(t =>
    (!search || t.name.toLowerCase().includes(search.toLowerCase())) &&
    isCombinable(t.name)
  );

  // Group tables by schema for the collapsible data tree
  const schemaGroups = visibleTables.reduce((acc, t) => {
    const s = t.schema || "DEFAULT";
    (acc[s] = acc[s] || []).push(t);
    return acc;
  }, {});
  const schemaNames = Object.keys(schemaGroups).sort();
  const toggleSchema = (name) =>
    setCollapsedSchemas(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  return (
    <Box sx={{ bgcolor:"#eef0f5", minHeight:"100vh", display:"flex", flexDirection:"column" }}>
      {/* ===== HEADER ===== */}
      <Box sx={{ bgcolor:"#fff", px:2.5, py:1.25, boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
                 display:"flex", alignItems:"center", justifyContent:"space-between", gap:2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontWeight:700, fontSize:15, color:"#222" }}>Report Builder</Typography>
          <Stack direction="row" spacing={0.25}>
            {["Data","Visualize","Present"].map((t,i) => (
              <Button key={t} size="small"
                sx={{ color: i===1 ? MEAS_C : "#777", fontWeight: i===1 ? 700 : 400,
                      fontSize:12, textTransform:"none", minWidth:0, px:1.25,
                      borderBottom: i===1 ? `2px solid ${MEAS_C}` : "none", borderRadius:0 }}>
                {t}
              </Button>
            ))}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {activeFilters.map((f,i) => (
            <Chip key={i} size="small"
              label={`${f.column}: ${f.values.slice(0,2).join(",")}${f.values.length>2?"…":""}`}
              onDelete={() => setActiveFilters(p => p.filter((_,j) => j!==i))}
              sx={{ bgcolor:"#fff3e0", color:"#e65100", fontSize:11 }}/>
          ))}
          <Tooltip title="Refresh tables">
            <IconButton size="small" onClick={() => { setLoadingTables(true); fetchTables().then(t=>{setTables(t);setLoadingTables(false);}); }}>
              <RefreshIcon sx={{ fontSize:18 }}/>
            </IconButton>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<FolderOpenIcon sx={{fontSize:16}}/>}
            onClick={openReportsBrowser}>
            Catalog
          </Button>
          <Button size="small" variant="outlined" startIcon={<SaveIcon sx={{fontSize:16}}/>}
            onClick={() => { if (!valueFields.length || !rowFields.length) { setErr("Build a report first (a measure + a dimension)."); return; } setSaveDlg(true); }}>
            Save
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon sx={{fontSize:16}}/>}
            onClick={exportCsv} disabled={!result && !aiResult}>
            Export CSV
          </Button>
          <Button variant="contained" onClick={run} disabled={running}
            startIcon={running ? <CircularProgress size={14} color="inherit"/> : <PlayArrowIcon sx={{fontSize:18}}/>}
            sx={{ bgcolor:MEAS_C, borderRadius:1, textTransform:"none",
                  transition:"transform .12s ease, box-shadow .12s ease, background-color .12s ease",
                  boxShadow:"0 2px 6px rgba(37,99,235,0.25)",
                  "&:hover":{ bgcolor:"#1741a6", transform:"translateY(-2px)", boxShadow:"0 6px 16px rgba(37,99,235,0.4)" },
                  "&:active":{ transform:"translateY(0)", boxShadow:"0 2px 6px rgba(37,99,235,0.25)" } }}>
            {running ? "Running…" : "Run"}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ flex:1, display:"flex", minHeight:0 }}>
        {/* ===== LEFT — Dark data panel ===== */}
        <Box sx={{ width:leftW, order:1, bgcolor:PANEL_BG, display:"flex", flexDirection:"column",
                   overflow:"hidden", flexShrink:0 }}>
          <Box sx={{ p:1.5, pb:1 }}>
            <Typography sx={{ color:"#6b7a99", fontSize:10, fontWeight:700, letterSpacing:1,
                               textTransform:"uppercase", mb:1 }}>
              Subject Area
            </Typography>
            <Box sx={{ bgcolor:"#2a3347", borderRadius:1, px:1.25, py:0.6, mb:1 }}>
              <Typography sx={{ color:"#7eb6f0", fontSize:11.5, fontFamily:"monospace" }}>
                Oracle FREE · AIDATALOOKS_DW
              </Typography>
            </Box>
            {/* Search */}
            <Box sx={{ bgcolor:"#2a3347", borderRadius:1, px:1.25, py:0.5, display:"flex", alignItems:"center", gap:0.5 }}>
              <Typography sx={{ color:"#6b7a99", fontSize:13 }}>🔍</Typography>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search tables…"
                style={{ background:"transparent", border:"none", outline:"none",
                         color:PANEL_TEXT, fontSize:12, flex:1, fontFamily:"inherit" }}
              />
            </Box>
            {/* Friendly vs original DW names */}
            <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mt:0.75 }}>
              <Typography sx={{ color:"#6b7a99", fontSize:10.5 }}>
                {showOriginal ? "Showing database names" : "Showing business names"}
              </Typography>
              <Tooltip title="Toggle between friendly business names and original DW table/column names. Tip: right-click any name to see its DW name.">
                <Switch size="small" checked={showOriginal}
                  onChange={e => setShowOriginal(e.target.checked)}
                  sx={{ "& .MuiSwitch-track":{ bgcolor:"#556677" } }}/>
              </Tooltip>
            </Box>
            {/* Combinability hint: explain why some tables are hidden */}
            {reachable && (
              <Box sx={{ mt:0.75, px:1, py:0.5, bgcolor:"#1f3a2b", borderRadius:1,
                         display:"flex", alignItems:"center", gap:0.5 }}>
                <VerifiedIcon sx={{ fontSize:13, color:"#7fd6a3" }}/>
                <Typography sx={{ color:"#9ee0b8", fontSize:10, lineHeight:1.3 }}>
                  Showing only tables that join to{" "}
                  <b>{valueFields[0]?.label || valueFields[0]?.column}</b>. Remove the measure to see all.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex:1, overflow:"auto", pb:2 }}>
            {loadingTables ? (
              <Box sx={{ p:3, textAlign:"center" }}><CircularProgress size={20} sx={{ color:ACCENT }}/></Box>
            ) : schemaNames.map(schema => (
              <Box key={schema}>
                {/* Schema group header (collapsible) */}
                <Box onClick={() => toggleSchema(schema)}
                  sx={{ px:1, py:0.6, display:"flex", alignItems:"center", gap:0.5, cursor:"pointer",
                        bgcolor:"#2a3347", borderTop:"1px solid #1a2030",
                        "&:hover":{ bgcolor:"#313c52" } }}>
                  {collapsedSchemas.has(schema)
                    ? <ChevronRightIcon sx={{ fontSize:15, color:"#7eb6f0" }}/>
                    : <ExpandMoreIcon sx={{ fontSize:15, color:"#7eb6f0" }}/>}
                  <StorageIcon sx={{ fontSize:13, color:"#7eb6f0" }}/>
                  <Typography sx={{ color:"#cfe0f5", fontSize:11.5, fontWeight:700, fontFamily:"monospace",
                                    flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {schema}
                  </Typography>
                  <Typography sx={{ color:"#6b7a99", fontSize:10 }}>
                    {schemaGroups[schema].length}
                  </Typography>
                </Box>

                {/* Tables in this schema */}
                {!collapsedSchemas.has(schema) && schemaGroups[schema].map(t => (
              <Box key={t.name}>
                {/* Table header row */}
                <Box onClick={() => toggleTable(t.name)}
                  onContextMenu={e => openCtx(e, t.name, t.label)}
                  title={isCombinable(t.name) ? t.name : `${t.name} — not connected to your selected measure`}
                  sx={{ px:1.5, py:0.7, display:"flex", alignItems:"center", gap:0.75,
                        cursor:"pointer", opacity: isCombinable(t.name) ? 1 : 0.38,
                        "&:hover":{ bgcolor:"#2a3347" },
                        bgcolor: expanded.has(t.name) ? "#253550" : "transparent" }}>
                  {expanded.has(t.name)
                    ? <ExpandMoreIcon sx={{ fontSize:14, color:ACCENT }}/>
                    : <ChevronRightIcon sx={{ fontSize:14, color:"#556677" }}/>}
                  <Typography title={t.name} sx={{ color:PANEL_TEXT, fontSize:12.5, flex:1,
                                    fontFamily: showOriginal ? "monospace" : "inherit",
                                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {disp(t.name, t.label)}
                  </Typography>
                  <Typography sx={{ color:"#556677", fontSize:10 }}>
                    {t.rows ? (t.rows>=1000 ? Math.round(t.rows/1000)+"k" : t.rows) : ""}
                  </Typography>
                  <Chip size="small" label={t.role||"?"}
                    sx={{ height:15, fontSize:9, ml:0.5,
                          color: t.role==="fact"?"#60a5fa":"#34d399",
                          bgcolor: t.role==="fact"?"#1e3a5f":"#064e3b" }}/>
                </Box>

                {/* Expanded columns */}
                {expanded.has(t.name) && (
                  <Box sx={{ pl:0.5 }}>
                    {loadingCols[t.name] ? (
                      <Box sx={{ pl:4, py:1 }}><CircularProgress size={13} sx={{ color:ACCENT }}/></Box>
                    ) : tableColumns[t.name] ? (
                      <>
                        {/* Measures section */}
                        {tableColumns[t.name].measures.length > 0 && (
                          <>
                            <Box sx={{ px:2, pt:0.5, pb:0.25, display:"flex", alignItems:"center", gap:0.5 }}>
                              <FunctionsIcon sx={{ fontSize:10, color:"#60a5fa" }}/>
                              <Typography sx={{ color:"#4d8fd4", fontSize:9.5, fontWeight:700, letterSpacing:0.6, textTransform:"uppercase" }}>
                                Measures
                              </Typography>
                            </Box>
                            {tableColumns[t.name].measures.map(m => (
                              <Box key={m.name} draggable title={`${m.name} — click or drag to add`}
                                onContextMenu={e => openCtx(e, m.name, m.label)}
                                onClick={() => clickAdd({ table:t.name, column:m.name, label:m.label, kind:"measure" })}
                                onDragStart={e => handleDragStart(e, { table:t.name, column:m.name, label:m.label, kind:"measure" })}
                                sx={{ pl:3.5, pr:1.5, py:0.35, display:"flex", alignItems:"center", gap:0.5,
                                      cursor:"pointer", "&:hover":{ bgcolor:"#253550" }, borderRadius:0.5, mx:0.5 }}>
                                <FunctionsIcon sx={{ fontSize:11, color:"#60a5fa", flexShrink:0 }}/>
                                <Typography sx={{ color:"#a5c8f0", fontSize:11.5,
                                                  fontFamily: showOriginal ? "monospace" : "inherit",
                                                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                  {disp(m.name, m.label)}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* Dimensions section */}
                        {tableColumns[t.name].dimensions.length > 0 && (
                          <>
                            <Box sx={{ px:2, pt:0.75, pb:0.25, display:"flex", alignItems:"center", gap:0.5 }}>
                              <TextFieldsIcon sx={{ fontSize:10, color:"#34d399" }}/>
                              <Typography sx={{ color:"#2d9e72", fontSize:9.5, fontWeight:700, letterSpacing:0.6, textTransform:"uppercase" }}>
                                Dimensions
                              </Typography>
                            </Box>
                            {[...tableColumns[t.name].dimensions]
                              .sort((a,b) => (a.is_key?1:0) - (b.is_key?1:0))
                              .map(d => (
                              <Box key={d.name} draggable title={`${d.name} — click or drag to add`}
                                onContextMenu={e => openCtx(e, d.name, d.label)}
                                onClick={() => clickAdd({ table:t.name, column:d.name, label:d.label, kind:"dimension" })}
                                onDragStart={e => handleDragStart(e, { table:t.name, column:d.name, label:d.label, kind:"dimension" })}
                                sx={{ pl:3.5, pr:1.5, py:0.35, display:"flex", alignItems:"center", gap:0.5,
                                      cursor:"pointer", opacity: d.is_key ? 0.5 : 1,
                                      "&:hover":{ bgcolor:"#253550", opacity:1 }, borderRadius:0.5, mx:0.5 }}>
                                {d.is_key
                                  ? <KeyIcon sx={{ fontSize:10, color:"#6b7a99", flexShrink:0 }}/>
                                  : <TextFieldsIcon sx={{ fontSize:11, color:"#34d399", flexShrink:0 }}/>}
                                <Typography sx={{ color: d.is_key ? "#7e8ca3" : "#9dd4bc", fontSize:11.5,
                                                  fontFamily: showOriginal ? "monospace" : "inherit",
                                                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                  {disp(d.name, d.label)}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        )}
                      </>
                    ) : null}
                  </Box>
                )}
              </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Splitter: Data | Canvas — drag to resize both panels */}
        <Box onMouseDown={startResize("left")} title="Drag to resize panels"
          sx={{ width:10, order:2, flexShrink:0, cursor:"col-resize", bgcolor:"#cfd6e3",
                display:"flex", alignItems:"center", justifyContent:"center",
                "&:hover":{ bgcolor:ACCENT }, transition:"background 0.15s",
                "&:hover .grip":{ bgcolor:"#fff" } }}>
          <Box className="grip" sx={{ width:2, height:30, bgcolor:"#94a3b8", borderRadius:1,
                                       boxShadow:"3px 0 0 #94a3b8, -3px 0 0 #94a3b8" }} />
        </Box>

        {/* ===== CENTER — Canvas (rendered last via flex order) ===== */}
        <Box sx={{ flex:1, order:5, minWidth:0, display:"flex", flexDirection:"column", p:1.5, gap:1.5, overflow:"auto" }}>
          {/* AI Query bar */}
          <AIQueryBar onResult={d => setAiResult(d)} onError={e => setErr(e)} />

          {/* ===== OAC-style "Selected Columns" criteria bar (moved below the chart) ===== */}
          <Paper variant="outlined"
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const it = dragItem.current; dragItem.current = null;
                           if (it) { it.kind === "dimension" ? clickAdd(it) : clickAdd(it); } }}
            sx={{ order:2, p:1, bgcolor:"#fff", borderStyle: (valueFields.length||rowFields.length||colFields.length||activeFilters.length) ? "solid":"dashed",
                  borderColor:"#cdd6e6" }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb:0.5 }}>
              <ViewColumnIcon sx={{ fontSize:14, color:MEAS_C }}/>
              <Typography sx={{ fontSize:10, fontWeight:700, color:"#555", letterSpacing:0.6, textTransform:"uppercase" }}>
                Selected Columns
              </Typography>
            </Stack>
            {(valueFields.length||rowFields.length||colFields.length||activeFilters.length) === 0 ? (
              <Typography sx={{ fontSize:11.5, color:"#aaa", py:0.5 }}>
                Click or drag columns from the left — they appear here as your report criteria, then press Run.
              </Typography>
            ) : (
              <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.75 }}>
                {valueFields.map((f,i) => (
                  <CriteriaCard key={"v"+i} label={`${agg}(${f.alias||f.label||f.column})`} kind="measure"
                    currentFormat={f.format}
                    onRename={name => setValueFields(p => p.map((it,j)=> j===i ? {...it, alias:name} : it))}
                    onFormat={fmt => setValueFields(p => p.map((it,j)=> j===i ? {...it, format:fmt} : it))}
                    onRemove={() => setValueFields(p => p.filter((_,j)=>j!==i))}/>
                ))}
                {rowFields.map((f,i) => (
                  <CriteriaCard key={"r"+i} label={f.alias||f.label||f.column} kind="dimension"
                    onRename={name => setRowFields(p => p.map((it,j)=> j===i ? {...it, alias:name} : it))}
                    onAddFilter={() => addFilterField(f)}
                    moveLabel="Move to Columns (pivot)"
                    onMove={() => { setColFields(p => p.find(x=>x.table===f.table&&x.column===f.column)?p:[...p,f]); setRowFields(p => p.filter((_,j)=>j!==i)); if(chartType!=="pivot") setChartType("pivot"); }}
                    onRemove={() => setRowFields(p => p.filter((_,j)=>j!==i))}/>
                ))}
                {colFields.map((f,i) => (
                  <CriteriaCard key={"c"+i} label={f.alias||f.label||f.column} kind="column"
                    onRename={name => setColFields(p => p.map((it,j)=> j===i ? {...it, alias:name} : it))}
                    onAddFilter={() => addFilterField(f)}
                    moveLabel="Move to Rows"
                    onMove={() => { setRowFields(p => p.find(x=>x.table===f.table&&x.column===f.column)?p:[...p,f]); setColFields(p => p.filter((_,j)=>j!==i)); }}
                    onRemove={() => setColFields(p => p.filter((_,j)=>j!==i))}/>
                ))}
              </Box>
            )}
            {/* Filter value pickers (folded in from the removed config panel) */}
            {(activeFilters.length > 0 || filterLoading) && (
              <Box sx={{ mt:1, pt:1, borderTop:"1px dashed #e3c9a0", display:"flex", flexWrap:"wrap", gap:1.5, alignItems:"flex-start" }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt:0.75 }}>
                  <FilterAltIcon sx={{ fontSize:14, color:"#d97706" }}/>
                  <Typography sx={{ fontSize:10, fontWeight:700, color:"#d97706", textTransform:"uppercase" }}>Filters</Typography>
                  {filterLoading && <CircularProgress size={11} sx={{ color:"#d97706" }}/>}
                </Stack>
                {activeFilters.map((f, i) => (
                  <Box key={`${f.table}.${f.column}`} sx={{ minWidth:180 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:0.25 }}>
                      <Typography sx={{ fontSize:10.5, fontWeight:700, color:"#e65100", fontFamily:"monospace" }}>
                        {f.label || f.column}
                      </Typography>
                      <CloseIcon onClick={() => removeFilter(i)} sx={{ fontSize:13, color:"#bbb", cursor:"pointer", "&:hover":{ color:"#e65100" } }}/>
                    </Stack>
                    <FormControl size="small" fullWidth>
                      <Select multiple displayEmpty value={f.values}
                        onChange={e => setFilterValues(i, typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                        renderValue={sel => sel.length === 0
                          ? <span style={{ color:"#aaa", fontSize:12 }}>All values</span>
                          : <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.25 }}>{sel.map(v => <Chip key={v} label={v} size="small" sx={{ height:18, fontSize:10 }}/>)}</Box>}
                        sx={{ fontSize:12, bgcolor:"#fff" }} MenuProps={{ PaperProps:{ sx:{ maxHeight:280 } } }}>
                        {(f.options || []).map(opt => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize:12, py:0.25 }}>
                            <Checkbox size="small" checked={f.values.includes(opt)} sx={{ p:0.5 }}/>{opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Chart type toolbar (Views) */}
          <Paper variant="outlined" sx={{ order:1, p:1, display:"flex", alignItems:"center", gap:0.5, flexWrap:"wrap", bgcolor:"#fff" }}>
            <Typography sx={{ fontSize:10, fontWeight:700, color:"#777", mr:0.5, letterSpacing:0.6, textTransform:"uppercase" }}>
              Type
            </Typography>
            {CHART_TYPES.map(ct => (
              <Tooltip key={ct.id} title={ct.label} placement="top">
                <IconButton size="small" onClick={() => setChartType(ct.id)}
                  sx={{ borderRadius:1.5, p:0.6,
                        transition:"transform .12s ease, background-color .12s ease, box-shadow .12s ease",
                        bgcolor: chartType===ct.id ? MEAS_C : "transparent",
                        color:   chartType===ct.id ? "#fff" : "#666",
                        boxShadow: chartType===ct.id ? "0 2px 8px rgba(37,99,235,0.35)" : "none",
                        "&:hover":{ bgcolor: chartType===ct.id ? MEAS_C : "#f0f4ff",
                                    color:   chartType===ct.id ? "#fff" : MEAS_C,
                                    transform:"translateY(-2px) scale(1.08)" } }}>
                  {ct.icon}
                </IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx:0.5 }}/>
            <Typography sx={{ fontSize:10, fontWeight:700, color:"#777", letterSpacing:0.6, textTransform:"uppercase" }}>
              Agg
            </Typography>
            <FormControl size="small">
              <Select value={agg} onChange={e => setAgg(e.target.value)}
                sx={{ fontSize:12, height:28, minWidth:75, "& .MuiSelect-select":{ py:0.4 } }}>
                {AGGS.map(a => <MenuItem key={a} value={a} sx={{ fontSize:12 }}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          {/* Canvas area */}
          <Paper variant="outlined" sx={{ order:3, flex:1, minHeight:360, p:2, overflow:"auto", bgcolor:"#fff" }}>
            {renderCanvas()}
          </Paper>

          {/* Trust line — reassure non-technical users the numbers are correct */}
          {(result?.sql || aiResult?.sql) && (
            <Paper variant="outlined" sx={{ order:4, px:1.5, py:0.75, bgcolor:"#f1f8f4", borderColor:"#bfe3cc" }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <VerifiedIcon sx={{ fontSize:16, color:"#2e7d32" }}/>
                <Typography sx={{ fontSize:11.5, color:"#2e7d32", fontWeight:600 }}>
                  Verified — totals come from a single measure over data-checked joins, so they reconcile to the source.
                </Typography>
                <Button size="small" onClick={() => setShowSql(s => !s)}
                  sx={{ fontSize:10, color:"#789", textTransform:"none", minWidth:0, ml:"auto" }}>
                  {showSql ? "Hide query" : "Show query"}
                </Button>
              </Stack>
              {showSql && (
                <Typography sx={{ fontSize:10.5, color:"#999", fontFamily:"monospace",
                                  mt:0.75, display:"block", whiteSpace:"pre-wrap", wordBreak:"break-all" }}>
                  {result?.sql || aiResult?.sql}
                </Typography>
              )}
            </Paper>
          )}
        </Box>

      </Box>

      {/* Save Report dialog */}
      <Dialog open={saveDlg} onClose={() => setSaveDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize:16, fontWeight:700 }}>
          {currentReportId ? "Update report" : "Save report"}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Report name" value={reportName}
            onChange={e => setReportName(e.target.value)} sx={{ mt:1, mb:2 }}/>
          <Typography sx={{ fontSize:12, fontWeight:600, color:"#666", mb:0.5 }}>Folder</Typography>
          <ToggleButtonGroup exclusive size="small" value={saveFolder}
            onChange={(e,v) => v && setSaveFolder(v)} fullWidth>
            <ToggleButton value="custom">Custom (mine)</ToggleButton>
            <ToggleButton value="shared">Shared (everyone)</ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setSaveDlg(false)}>Cancel</Button>
          {currentReportId && (
            <Button onClick={() => { setCurrentReportId(null); doSave(); }}>Save as new</Button>
          )}
          <Button variant="contained" onClick={doSave} sx={{ bgcolor:MEAS_C }}>
            {currentReportId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Report browser */}
      <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize:16, fontWeight:700 }}>Report Catalog</DialogTitle>
        <DialogContent dividers>
          {reportsLoading ? (
            <Box sx={{ textAlign:"center", py:3 }}><CircularProgress size={24}/></Box>
          ) : reportsList.length === 0 ? (
            <Typography sx={{ color:"#999", py:2, textAlign:"center" }}>No saved reports yet.</Typography>
          ) : (
            ["shared","custom"].map(folder => {
              const items = reportsList.filter(r => r.folder === folder);
              if (!items.length) return null;
              return (
                <Box key={folder} sx={{ mb:1.5 }}>
                  <Typography sx={{ fontSize:11, fontWeight:700, color:MEAS_C, textTransform:"uppercase",
                                    letterSpacing:0.5, mb:0.5, display:"flex", alignItems:"center", gap:0.5 }}>
                    <FolderOpenIcon sx={{ fontSize:15 }}/> {folder} folder ({items.length})
                  </Typography>
                  <List dense disablePadding>
                    {items.map(r => (
                      <ListItemButton key={r.id} onClick={() => loadReport(r.id)}
                        sx={{ borderRadius:1, "&:hover":{ bgcolor:"#f0f4ff" } }}>
                        <ListItemText primary={r.name}
                          secondary={r.updated_at ? new Date(r.updated_at).toLocaleString() : ""}
                          primaryTypographyProps={{ fontSize:13.5, fontWeight:600 }}
                          secondaryTypographyProps={{ fontSize:11 }}/>
                        <IconButton size="small" onClick={ev => removeReport(r.id, ev)}>
                          <DeleteOutlineIcon sx={{ fontSize:17, color:"#c62828" }}/>
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDlg(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Right-click: show original DW name */}
      <Menu open={!!ctxMenu} onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}>
        <Box sx={{ px:2, py:1, minWidth:220 }}>
          <Typography sx={{ fontSize:10, color:"#999", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>
            Business name
          </Typography>
          <Typography sx={{ fontSize:13, color:"#222", mb:1 }}>{ctxMenu?.label || ctxMenu?.physical}</Typography>
          <Typography sx={{ fontSize:10, color:"#999", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>
            Original DW name
          </Typography>
          <Typography sx={{ fontSize:13, color:MEAS_C, fontFamily:"monospace" }}>{ctxMenu?.physical}</Typography>
        </Box>
        <MenuItem onClick={() => { navigator.clipboard?.writeText(ctxMenu?.physical || ""); setCtxMenu(null); setMsg("Copied DW name"); }}
          sx={{ fontSize:12.5, gap:1 }}>
          <ContentCopyIcon sx={{ fontSize:15 }}/> Copy original name
        </MenuItem>
      </Menu>

      <Snackbar open={!!msg} autoHideDuration={2500} onClose={() => setMsg("")}
        anchorOrigin={{ vertical:"bottom", horizontal:"center" }}>
        <Alert severity="success" onClose={() => setMsg("")}>{msg}</Alert>
      </Snackbar>
      <Snackbar open={!!err} autoHideDuration={5000} onClose={() => setErr("")}
        anchorOrigin={{ vertical:"bottom", horizontal:"center" }}>
        <Alert severity="error" onClose={() => setErr("")}>{err}</Alert>
      </Snackbar>
    </Box>
  );
}
